// Anticipos de sueldo (se descuentan en el rol). Nómina → Movimientos.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, CalendarClock, Calculator, Loader2, Plus, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { Campo, MESES, Modal, fieldCls, fecha, money, nomRpc, useDialogoEnLinea, type AnticipoRow, type EmpleadoRow, hoyISO } from './shared';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';

const ESTADOS: Record<string, string> = { solicitado: 'Pendiente de aprobación', pendiente: 'Pendiente', descontado: 'Descontado en rol', anulado: 'Anulado', rechazado: 'Rechazado' };
const ORIGENES: Record<string, string> = { manual: 'Manual', quincena: 'Quincena', semana: 'Semana' };
const vacio = { empleado_id: '', fecha: hoyISO(), monto: '', motivo: '' };
const HOY = new Date();
const corridaVacia = { tipo: 'quincena', anio: HOY.getFullYear(), mes: HOY.getMonth() + 1, pct: '50', fecha: hoyISO() };

interface ResultadoCorrida {
  tipo: string; periodo: string; fecha_pago: string; generados: number; total: number;
  omitidos_duplicado: number; omitidos_tope: number; omitidos_sin_dias: number; rol_en_borrador: boolean | null;
}

export function Anticipos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { isAdmin } = usePermissions();
  const [estado, setEstado] = useState('solicitado');
  const { data: rows = [], isFetching } = useQuery<AnticipoRow[]>({
    queryKey: ['nom-anticipos', estado],
    queryFn: () => nomRpc<AnticipoRow[]>('nom_anticipos', { _empleado_id: null, _estado: estado }),
  });
  const { data: empleados = [] } = useQuery<EmpleadoRow[]>({
    queryKey: ['nom-empleados-opt-act'],
    queryFn: () => nomRpc<EmpleadoRow[]>('nom_empleados', { _q: null, _solo_activos: true }),
  });

  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [corridaAbierta, setCorridaAbierta] = useState(false);
  const [corrida, setCorrida] = useState<typeof corridaVacia>(corridaVacia);

  const generar = useMutation({
    mutationFn: () => nomRpc<ResultadoCorrida>('nom_anticipos_generar', {
      _data: corrida.tipo === 'quincena'
        ? { tipo: 'quincena', anio: corrida.anio, mes: corrida.mes, pct: Number(corrida.pct) || 50 }
        : { tipo: 'semana', fecha: corrida.fecha },
    }),
    onSuccess: (r) => {
      const omitidos = r.omitidos_duplicado + r.omitidos_tope + r.omitidos_sin_dias;
      toast.success(`${r.periodo}: ${r.generados} anticipos generados por ${money(r.total)} (pago ${fecha(r.fecha_pago)})${omitidos ? ` — ${omitidos} omitidos` : ''}`);
      if (r.omitidos_duplicado) toast.info(`${r.omitidos_duplicado} ya tenían el período generado`);
      if (r.omitidos_tope) toast.warning(`${r.omitidos_tope} omitidos por tope (anticipos del mes superarían el sueldo)`);
      if (r.omitidos_sin_dias) toast.info(`${r.omitidos_sin_dias} sin días laborables en la semana`);
      if (r.rol_en_borrador) toast.info('El rol del mes está en borrador: recalcúlalo para que descuente estos anticipos');
      setCorridaAbierta(false);
      void qc.invalidateQueries({ queryKey: ['nom-anticipos'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const guardar = useMutation({
    mutationFn: () => nomRpc('nom_anticipo_guardar', { _data: { ...form, monto: Number(form.monto), motivo: form.motivo || null } }),
    onSuccess: () => { toast.success('Anticipo registrado (pendiente de aprobación)'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['nom-anticipos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const anular = useMutation({
    mutationFn: (id: string) => nomRpc('nom_anticipo_anular', { _id: id }),
    onSuccess: () => { toast.success('Anticipo anulado'); void qc.invalidateQueries({ queryKey: ['nom-anticipos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const aprobar = useMutation({
    mutationFn: (id: string) => nomRpc('nom_anticipo_aprobar', { _id: id }),
    onSuccess: () => { toast.success('Anticipo aprobado (pendiente de descuento en rol)'); void qc.invalidateQueries({ queryKey: ['nom-anticipos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const rechazar = useMutation({
    mutationFn: async (id: string) => {
      const v = await dlg.pedir('Motivo del rechazo', [{ key: 'motivo', label: 'Motivo', minimo: 0 }]);
      if (v === null) return Promise.reject(new Error('Cancelado'));
      return nomRpc('nom_anticipo_rechazar', { _id: id, _motivo: v.motivo || null });
    },
    onSuccess: () => { toast.success('Anticipo rechazado'); void qc.invalidateQueries({ queryKey: ['nom-anticipos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Contabilizar corridas ya generadas (asiento DEBE anticipos / HABER banco por período).
  const [contabAbierto, setContabAbierto] = useState(false);
  const { data: todosAnticipos = [] } = useQuery<AnticipoRow[]>({
    queryKey: ['nom-anticipos-todos-contab'],
    queryFn: () => nomRpc<AnticipoRow[]>('nom_anticipos', { _empleado_id: null, _estado: null }),
    enabled: contabAbierto,
  });
  const corridas = Object.values(
    todosAnticipos
      .filter((a) => (a.origen === 'semana' || a.origen === 'quincena') && a.estado !== 'anulado' && a.periodo)
      .reduce<Record<string, { periodo: string; tipo: string; total: number; n: number }>>((acc, a) => {
        const k = a.periodo as string;
        acc[k] = acc[k] ?? { periodo: k, tipo: a.origen, total: 0, n: 0 };
        acc[k].total += Number(a.monto); acc[k].n += 1;
        return acc;
      }, {}),
  ).sort((x, y) => y.periodo.localeCompare(x.periodo));
  const contabilizar = useMutation({
    mutationFn: (periodo: string) => nomRpc<{ numero: number; total: number; trabajadores: number; reemplazado: boolean }>('nom_anticipos_contabilizar', { _periodo: periodo }),
    onSuccess: (r) => toast.success(`Asiento #${r.numero}: ${r.trabajadores} trabajadores por ${money(r.total)}${r.reemplazado ? ' (actualizado)' : ''}`),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const total = rows.reduce((s, a) => s + Number(a.monto), 0);

  const headersExp = ['N°', 'Fecha', 'Empleado', 'Monto', 'Motivo', 'Origen', 'Estado'];
  const MONEYCOLS = [3];
  const filasExport: Celda[][] = [
    ...rows.map((a, i): Celda[] => [
      i + 1, fecha(a.fecha), a.razon_social, Number(a.monto), a.motivo ?? '',
      `${ORIGENES[a.origen] ?? a.origen}${a.periodo ? ` · ${a.periodo}` : ''}`, ESTADOS[a.estado] ?? a.estado,
    ]),
    ['', '', 'TOTAL', total, '', '', ''],
  ];

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Anticipos</h1>
        <div className="flex items-center gap-2">
          <ExportarBotones
            nombre={`anticipos-${fechaLocal()}`} titulo="Anticipos"
            headers={headersExp} filas={filasExport} colsDerecha={[0, 3]} disabled={rows.length === 0}
            fmtPDF={(v, c) => (MONEYCOLS.includes(c) ? (v === '' ? '' : money(Number(v))) : String(v ?? ''))}
          />
          <Button variant="outline" onClick={() => { setCorrida(corridaVacia); setCorridaAbierta(true); }}>
            <CalendarClock className="w-4 h-4 mr-1" /> Generar corrida
          </Button>
          <Button variant="outline" onClick={() => setContabAbierto(true)}>
            <Calculator className="w-4 h-4 mr-1" /> Contabilizar corridas
          </Button>
          <Button onClick={() => { setForm(vacio); setAbierto(true); }}><Plus className="w-4 h-4 mr-1" /> Nuevo anticipo</Button>
        </div>
      </div>

      <select className={`${fieldCls} w-56`} value={estado} onChange={(e) => setEstado(e.target.value)} aria-label="Estado">
        <option value="solicitado">Pendientes de aprobación</option>
        <option value="pendiente">Pendientes (aprobados)</option>
        <option value="descontado">Descontados</option>
        <option value="anulado">Anulados</option>
        <option value="rechazado">Rechazados</option>
        <option value="todos">Todos</option>
      </select>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Anticipos ({rows.length}) — total {money(total)}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Empleado</th><th className="py-2 pr-3 text-right">Monto</th>
              <th className="py-2 pr-3">Motivo</th><th className="py-2 pr-3">Origen</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin anticipos</td></tr>}
              {rows.map((a) => (
                <tr key={a.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{fecha(a.fecha)}</td>
                  <td className="py-2 pr-3">{a.razon_social}<div className="text-xs text-muted-foreground font-mono">{a.identificacion}</div></td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(a.monto)}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{a.motivo}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${a.origen !== 'manual' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                      {ORIGENES[a.origen] ?? a.origen}{a.periodo ? ` · ${a.periodo}` : ''}
                    </span>
                  </td>
                  <td className="py-2 pr-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${a.estado === 'pendiente' ? 'bg-pendiente-soft text-pendiente' : a.estado === 'descontado' ? 'bg-completado-soft text-completado' : a.estado === 'solicitado' ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300' : 'bg-muted text-muted-foreground'}`}>
                      {ESTADOS[a.estado] ?? a.estado}
                    </span>
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {a.estado === 'solicitado' && isAdmin() && (
                      <>
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-completado hover:bg-completado-soft" title="Aprobar" onClick={async () => { if (await dlg.confirmar('¿Aprobar el anticipo?', { detalle: 'Se descontará en el próximo rol.', aceptar: 'Aprobar' })) aprobar.mutate(a.id); }}><CheckCircle2 className="w-4 h-4" /></button>
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Rechazar" onClick={() => rechazar.mutate(a.id)}><XCircle className="w-4 h-4" /></button>
                      </>
                    )}
                    {a.estado === 'pendiente' && (
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10" title="Anular" onClick={async () => { if (await dlg.confirmar('¿Anular anticipo?', { peligro: true, aceptar: 'Anular' })) anular.mutate(a.id); }}><Ban className="w-4 h-4" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title="Nuevo anticipo" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Empleado *">
              <select className={fieldCls} value={form.empleado_id} onChange={(e) => setForm({ ...form, empleado_id: e.target.value })}>
                <option value="">— Selecciona —</option>
                {empleados.map((e) => <option key={e.id} value={e.id}>{e.razon_social} ({e.identificacion})</option>)}
              </select>
            </Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Fecha *"><input type="date" className={fieldCls} value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} /></Campo>
              <Campo label="Monto ($) *"><input type="number" min="0" step="0.01" className={fieldCls} value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} /></Campo>
            </div>
            <Campo label="Motivo"><input className={fieldCls} value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} /></Campo>
            <div className="text-xs text-muted-foreground">El anticipo pendiente se descuenta automáticamente en el próximo rol de pagos.</div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.empleado_id || !Number(form.monto)} onClick={() => guardar.mutate()}>Registrar</Button>
            </div>
          </div>
        </Modal>
      )}

      {corridaAbierta && (
        <Modal title="Generar corrida de anticipos" onClose={() => setCorridaAbierta(false)}>
          <div className="space-y-3">
            <Campo label="Tipo de corrida">
              <select className={fieldCls} value={corrida.tipo} onChange={(e) => setCorrida({ ...corrida, tipo: e.target.value })}>
                <option value="quincena">Quincenal — empleados con frecuencia quincenal</option>
                <option value="semana">Semanal — empleados con frecuencia semanal</option>
              </select>
            </Campo>
            {corrida.tipo === 'quincena' ? (
              <div className="grid grid-cols-3 gap-3">
                <Campo label="Año"><input type="number" className={fieldCls} value={corrida.anio} onChange={(e) => setCorrida({ ...corrida, anio: Number(e.target.value) })} /></Campo>
                <Campo label="Mes">
                  <select className={fieldCls} value={corrida.mes} onChange={(e) => setCorrida({ ...corrida, mes: Number(e.target.value) })}>
                    {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                </Campo>
                <Campo label="% del sueldo"><input type="number" min="1" max="60" className={fieldCls} value={corrida.pct} onChange={(e) => setCorrida({ ...corrida, pct: e.target.value })} /></Campo>
              </div>
            ) : (
              <Campo label="Cualquier día de la semana a pagar">
                <input type="date" className={fieldCls} value={corrida.fecha} onChange={(e) => setCorrida({ ...corrida, fecha: e.target.value })} />
              </Campo>
            )}
            <div className="text-xs text-muted-foreground">
              {corrida.tipo === 'quincena'
                ? 'Genera el anticipo del 15 para cada empleado quincenal con contrato vigente. El rol mensual descuenta lo anticipado.'
                : 'Calcula sueldo/30 × días laborables reales de esa semana (turno asignado, sin feriados) y genera el anticipo con fecha de pago el viernes.'}
              {' '}No duplica períodos ya generados ni deja que lo anticipado del mes supere el sueldo.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setCorridaAbierta(false)}>Cancelar</Button>
              <Button isLoading={generar.isPending} onClick={() => generar.mutate()}>Generar</Button>
            </div>
          </div>
        </Modal>
      )}

      {contabAbierto && (
        <Modal title="Contabilizar corridas de anticipos" onClose={() => setContabAbierto(false)}>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Genera el asiento por corrida: <b>DEBE</b> Anticipos a empleados / <b>HABER</b> Banco. Es idempotente
              (re-contabilizar actualiza el asiento). Las corridas nuevas ya se contabilizan solas al generarlas.
            </p>
            {corridas.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No hay corridas semanales/quincenales.</p>
            ) : (
              <div className="max-h-[55vh] overflow-y-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted text-left text-xs text-muted-foreground">
                    <tr><th className="py-2 px-3">Período</th><th className="py-2 px-3">Trab.</th><th className="py-2 px-3 text-right">Total</th><th className="py-2 px-3"></th></tr>
                  </thead>
                  <tbody>
                    {corridas.map((c) => (
                      <tr key={c.periodo} className="border-t">
                        <td className="py-1.5 px-3 font-medium">{c.periodo} <span className="text-xs text-muted-foreground">({c.tipo})</span></td>
                        <td className="py-1.5 px-3 tabular-nums">{c.n}</td>
                        <td className="py-1.5 px-3 text-right tabular-nums">{money(c.total)}</td>
                        <td className="py-1.5 px-3 text-right">
                          <Button size="sm" variant="outline" isLoading={contabilizar.isPending && contabilizar.variables === c.periodo}
                            onClick={() => contabilizar.mutate(c.periodo)}>Contabilizar</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex justify-end"><Button variant="outline" onClick={() => setContabAbierto(false)}>Cerrar</Button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}
